


import React, { useState, useEffect } from "react";
import {
  getMonthWiseCustomerAcquisitonCostByDateAPI,
  getMonthWiseTicketSizeByDateAPI,
  getMonthWiseLoanApplicationByDateAPI,
  getMonthWiseCollectionEfficiencyByDateAPI,
  getMonthWiseIncomeRecievedAmountByDateAPI
} from "./ActionCreator";
import moment from "moment";
import Loader from "../../../presentationals/Loader/Loader.component";
import "./misDash.scss";
import DateFilterSelect from "../../../CustomDatePicker/DateFilterSelectold";

// ─── Constants ────────────────────────────────────────────────────
const SECTIONS = [
  {
    key: "cac",
    label: "Customer Acquisition Cost",
    shortLabel: "CAC",
    accent: "#2563EB",
    accentLight: "#EFF6FF",
    accentText: "#1E40AF",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M2 13c0-2.21 1.79-4 4-4s4 1.79 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M11 2v4M13 4H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "ticketSize",
    label: "Ticket Size",
    shortLabel: "Ticket",
    accent: "#059669",
    accentLight: "#ECFDF5",
    accentText: "#065F46",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="4" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5 8h6M5 10.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "approvedLoans",
    label: "Approved Loans",
    shortLabel: "Loans",
    accent: "#7C3AED",
    accentLight: "#F5F3FF",
    accentText: "#5B21B6",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 9l3.5 3.5L13 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    key: "collectionEfficiency",
    label: "Collection Efficiency",
    shortLabel: "Collection",
    accent: "#D97706",
    accentLight: "#FFFBEB",
    accentText: "#92400E",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 12 L5 8 L8 10 L11 5 L14 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11 7h3v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "IncomeEfficiency",
    label: "Income Efficiency",
    shortLabel: "Income",
    accent: "rgb(34, 216, 168)",
    accentLight: "#FFFBEB",
    accentText: "rgb(14, 146, 110)",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 12 L5 8 L8 10 L11 5 L14 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11 7h3v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const EXCLUDED_KEYS = ["Month_Name", "isLocked", "canEdit"];

// ─── Helpers ──────────────────────────────────────────────────────
const formatNumber = (val) => {
  const num = Number(val);
  if (val === undefined || val === null || val === "" || isNaN(num)) return "—";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const getKeys = (data) => {
  if (!data || data.length === 0) return [];
  return Object.keys(data[0]).filter((k) => !EXCLUDED_KEYS.includes(k));
};

const getAllMonths = (APIData) => {
  const seen = new Set();
  const months = [];
  SECTIONS.forEach(({ key }) => {
    (APIData[key] || []).forEach((item) => {
      if (item.Month_Name && !seen.has(item.Month_Name)) {
        seen.add(item.Month_Name);
        months.push(item.Month_Name);
      }
    });
  });
  return months;
};

// ─── KPI Summary Cards ────────────────────────────────────────────
function KPICards({ APIData }) {
  const kpis = SECTIONS.map(({ key, label, shortLabel, accent, accentLight, accentText, icon }) => {
    const data = APIData[key] || [];
    const count = data.length;
    const keys = getKeys(data);
    const firstKey = keys[0];
    let displayVal = "—";
    if (count > 0 && firstKey) {
      const vals = data.map((d) => Number(d[firstKey])).filter((n) => !isNaN(n));
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      if (avg !== null) displayVal = avg.toLocaleString("en-IN", { maximumFractionDigits: 0 });
    }
    return { label, shortLabel, accent, accentLight, accentText, icon, displayVal, count, firstKey };
  });

  return (
    <div className="mis-kpi-row">
      {kpis.map(({ label, accent, accentLight, accentText, icon, displayVal, count }, i) => (
        <div className="mis-kpi-card" key={i} style={{ "--accent": accent, "--accent-light": accentLight, "--accent-text": accentText }}>
          <div className="mis-kpi-top-bar" />
          <div className="mis-kpi-header">
            <span className="mis-kpi-icon" style={{ color: accent }}>{icon}</span>
            <span className="mis-kpi-badge" style={{ background: accentLight, color: accentText }}>
              {count > 0 ? `${count} months` : "No data"}
            </span>
          </div>
          <div className="mis-kpi-value">{displayVal}</div>
          <div className="mis-kpi-label">{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Unified Single Table ─────────────────────────────────────────
function UnifiedTable({ APIData }) {
  const allMonths = getAllMonths(APIData);

  const hasAnyData = SECTIONS.some(({ key }) => APIData[key] && APIData[key].length > 0);

  if (!hasAnyData) {
    return (
      <div className="mis-empty-state">
        <div className="mis-empty-icon">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <rect x="4" y="8" width="24" height="18" rx="3" stroke="#94A3B8" strokeWidth="1.5" />
            <path d="M4 13h24" stroke="#94A3B8" strokeWidth="1.5" />
            <path d="M10 18h12M10 22h7" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <p className="mis-empty-title">No data to display</p>
        <p className="mis-empty-sub">Select a date range and apply filters to load the dashboard.</p>
      </div>
    );
  }

  return (
    <div className="mis-table-outer">
      <div className="mis-table-scroll">
        <table className="mis-table">
          <thead>
            <tr>
              <th className="col-particulars">Particulars</th>
              {allMonths?.map((m, i) => (
                <th key={i} className="col-month">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS?.map(({ key, label, accent, accentLight, accentText, icon }) => {
              const data = APIData[key] || [];
              const keys = getKeys(data);
              const byMonth = {};
              data?.forEach((item) => { if (item.Month_Name) byMonth[item.Month_Name] = item; });

              return (
                <React.Fragment key={key}>
                  <tr className="mis-section-row">
                    <td colSpan={4} style={{position:'sticky',left:'0',zIndex:'1'}}>
                      <div className="mis-section-inner">
                        <span className="mis-section-dot" style={{ background: accent }} />
                        <span className="mis-section-icon" style={{ color: accent }}>{icon}</span>
                        <span className="mis-section-title" style={{ color: accentText,fontSize:window.innerWidth > 1800 ? "14px" :"12px" }}>{label}</span>
                        <span className="mis-section-pill" style={{ background: accentLight, color: accentText,fontSize:window.innerWidth > 1800 ? "12px" :"10px" }}>
                          {keys.length} metrics
                        </span>
                      </div>
                    </td>
                    <td colSpan={allMonths.length - 3}>
                     
                    </td>
                  </tr>

                  {keys.length === 0 ? (
                    <tr className="mis-nodata-row">
                      <td colSpan={allMonths.length + 1}>No data available</td>
                    </tr>
                  ) : (
                    keys?.map((k, i) => (
                      <tr className="mis-data-row" key={i} style={{padding:window.innerWidth > 1800 ? "14px 16px" : "10px 16px"}}>
                        <td className="mis-particulars-cell" style={{fontSize:window.innerWidth > 1800 ? "15.5px" :"12.5px",fontWeight:'600'}}>
                          <span className="mis-row-bar" style={{ background: accent }} />
                          {k.replace(/_/g, " ")}
                        </td>
                        {allMonths.map((m, j) => (
                          <td key={j} className="mis-value-cell" style={{fontSize:window.innerWidth > 1800 ? "15.5px" :"12.5px"}}>
                            {byMonth[m] !== undefined ? formatNumber(byMonth[m][k]) : "—"}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────
const CACDashboard = ({ nbfcList, admin, handleActiveFilteration, currentActiveFilters }) => {
  const [loader, setLoader] = useState(false);
  const [APIData, setAPIData] = useState({
    cac: [],
    ticketSize: [],
    approvedLoans: [],
    collectionEfficiency: [],
    IncomeEfficiency: [],
  });
  const [payload, setPayload] = useState({
    fromDate:  currentActiveFilters?.fromDate  ?? "",
    toDate:    currentActiveFilters?.toDate    ?? "",
    nbfcId:    currentActiveFilters?.nbfcId    ?? "",
    productId: currentActiveFilters?.productId ?? "",
  });

  useEffect(() => { getMonthWiseCustomerAcquisitonCostByDateFn(payload); }, []);

  useEffect(() => {
    handleActiveFilteration({
      fromDate:          payload?.fromDate  ?? "",
      toDate:            payload?.toDate    ?? "",
      nbfcId:            payload?.nbfcId    ?? "",
      productId:         payload?.productId ?? "",
      ActiveMonthFilter: "",
    });
  }, [payload]);

  const handleDate = (e, type) => {
    if (type === "applyFilter") {
      getMonthWiseCustomerAcquisitonCostByDateFn(payload);
    } else {
      setPayload((prev) => ({ ...prev, [type]: e.target.value }));
    }
  };

  const handleDateFilterPopup = (type, val) => {
      setPayload((prev) => ({ ...prev, [type]: val }));
  
      const formatted = moment(val).format("YYYY-MM-DD");
  
      setPayload((prev) => {
        if (prev[type] === formatted) return prev; // prevent useless update
  
        return {
          ...prev,
          [type]: formatted
        };
      });
  
    
    };

  const getMonthWiseCustomerAcquisitonCostByDateFn = (data) => {
    const sDate = moment(data?.fromDate).format("DD-MM-YYYY");
    const eDate = moment(data?.toDate).format("DD-MM-YYYY");
    let pending = 4;
    const done = () => { pending -= 1; if (pending === 0) setLoader(false); };
    setLoader(true);

    getMonthWiseCustomerAcquisitonCostByDateAPI(sDate, eDate, data?.nbfcId, data?.productId, (cb) => {
      setAPIData((prev) => ({ ...prev, cac: cb?.message === "success" && cb?.status === 200 ? cb.data : [] }));
      done();
    });
    getMonthWiseTicketSizeByDateAPI(sDate, eDate, data?.nbfcId, data?.productId, (cb) => {
      setAPIData((prev) => ({ ...prev, ticketSize: cb?.message === "success" && cb?.status === 200 ? cb.data : [] }));
      done();
    });
    getMonthWiseLoanApplicationByDateAPI(sDate, eDate, data?.nbfcId, data?.productId, (cb) => {
      setAPIData((prev) => ({ ...prev, approvedLoans: cb?.message === "success" && cb?.status === 200 ? cb.data : [] }));
      done();
    });
    getMonthWiseCollectionEfficiencyByDateAPI(sDate, eDate, data?.nbfcId, data?.productId, (cb) => {
      setAPIData((prev) => ({ ...prev, collectionEfficiency: cb?.message === "success" && cb?.status === 200 ? cb.data : [] }));
      done();
    });
    getMonthWiseIncomeRecievedAmountByDateAPI(sDate, eDate, data?.nbfcId, data?.productId, (cb) => {
      setAPIData((prev) => ({ ...prev, IncomeEfficiency: cb?.message === "success" && cb?.status === 200 ? cb.data : [] }));
      done();
    });
  };

  return (
    <div className="mis-dashboard">
      {loader && <Loader />}

      {/* ── Page Header ── */}
      <div className="mis-page-header">
        <div className="mis-page-header-left">
          <h1 className="mis-page-title">MIS Dashboard</h1>
          <p className="mis-page-sub">Month-wise performance overview</p>
        </div>
        <div className="mis-page-header-right">
          <span className="mis-live-badge">
            <span className="mis-live-dot" />
            Live
          </span>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="mis-filter-bar">
        <div className="mis-filter-group">
          <label className="mis-filter-label">From date - To date</label>
          
          <DateFilterSelect 
           value={[new Date(payload.fromDate), new Date(payload.toDate)]}
           isFilterCleared={false}
           onChange={(range) => {
                      handleDateFilterPopup("fromDate", range[0]);
                      handleDateFilterPopup("toDate", range[1]);
                    }}
                    customStyle={{border:'none',paddingLeft:'0'}}
          />
        </div>

        {/* <div className="mis-filter-divider" />

        <div className="mis-filter-group">
          <label className="mis-filter-label">To date</label>
          <input type="date" className="mis-filter-input" style={{fontSize:window.innerWidth > 1800 ? "16px" : "13px"}} value={payload?.toDate} onChange={(e) => handleDate(e, "toDate")} />
        </div> */}

        <div className="mis-filter-divider" />

        <div className="mis-filter-group">
          <label className="mis-filter-label">NBFC</label>
          <select className="mis-filter-input" style={{fontSize:window.innerWidth > 1800 ? "16px" : "13px"}} value={payload?.nbfcId} onChange={(e) => handleDate(e, "nbfcId")}>
            <option value="">All NBFCs</option>
            {nbfcList?.map((v) => (
              <option key={v.nbfcId} value={v.nbfcId}>{v.name}</option>
            ))}
          </select>
        </div>

        <div className="mis-filter-divider" />

        <div className="mis-filter-group">
          <label className="mis-filter-label">Product</label>
          <select className="mis-filter-input" style={{fontSize:window.innerWidth > 1800 ? "16px" : "13px"}} value={payload?.productId} onChange={(e) => handleDate(e, "productId")}>
            <option value="">All Products</option>
            <option value="loan">Loan</option>
          </select>
        </div>

        <button className="mis-apply-btn" onClick={(e) => handleDate(e, "applyFilter")}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
         
        </button>
      </div>

      {/* ── KPI Cards ── */}
     

      {/* ── Table Section ── */}
      <div className="mis-table-section">
        <div className="mis-table-section-header">
          <span className="mis-table-section-title">Month-wise breakdown</span>
          <span className="mis-table-section-hint">Scroll horizontally to see all months →</span>
        </div>
        <UnifiedTable APIData={APIData} />
      </div>
    </div>
  );
};

export default CACDashboard;